import { CheckCircle, ChevronDown, Info, Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactMarkdown from 'react-markdown';
import { END_CONVERSATION_SIGNAL, MAX_MESSAGE_LENGTH } from '../config/constants';
import useViewportHeight from '../hooks/useViewportHeight';
import { surveyAPI } from '../services/api'; // Use the new surveyAPI
import SurveyQRCode from './SurveyQRCode';

// Supported languages with emoji flags
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
];

const SurveyChatInterface = ({ user, sessionId, surveyTitle, surveyDescription, firstQuestion, isLoading = false, selectedLanguage = 'en', onLanguageChange }) => {
  // 1. Initialize messages with the first question from the server (or empty if still loading)
  const [messages, setMessages] = useState(
    firstQuestion
      ? [
        {
          id: 'initial',
          text: firstQuestion,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]
      : []
  );
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const tooltipRef = useRef(null);
  const languageDropdownRef = useRef(null);

  // Handle viewport height changes for mobile keyboard
  useViewportHeight();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Update messages when firstQuestion arrives or sessionId changes (language switch)
  useEffect(() => {
    if (firstQuestion) {
      setMessages([
        {
          id: 'initial',
          text: firstQuestion,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
      // Reset the hasUserSentMessage flag when session restarts
      setHasUserSentMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstQuestion, sessionId]);

  // Handle clicking outside tooltip to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  // Handle clicking outside language dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLanguageDropdown]);


  const preventBlur = (e) => {
    e.preventDefault();
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending || !sessionId) return;

    const userMessageText = inputMessage.trim();

    // Enforce character limit
    if (userMessageText.length > MAX_MESSAGE_LENGTH) {
      setError(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit`);
      return;
    }

    setInputMessage('');
    setError(null);

    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const userMessage = {
      id: Date.now(),
      text: userMessageText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);

    // Mark that user has sent their first message (disables language selector)
    if (!hasUserSentMessage) {
      setHasUserSentMessage(true);
    }

    try {
      const response = await surveyAPI.sendMessage(sessionId, userMessageText);

      const aiMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        toolCalls: response.tool_calls || [],
        toolResults: response.tool_results || []
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEndConversation = async () => {
    if (!window.confirm('Are you sure you want to end this conversation? Your feedback has been saved.')) {
      return;
    }

    setIsSending(true);
    setError(null);
    setInputMessage(''); // Clear input

    try {
      // Send the special signal to the backend
      const response = await surveyAPI.sendMessage(sessionId, END_CONVERSATION_SIGNAL);

      const aiMessage = {
        id: Date.now() + 1,
        text: response.message, // The backend's final "goodbye" message
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsEnded(true); // This triggers the "Hard End" in the UI
    } catch (err) {
      console.error('Failed to end conversation:', err);
      setError('Failed to end conversation. You can simply close this window.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="h-screen-mobile bg-nova-bg flex flex-col">
      {/* Minimalist header for Survey */}
      <div className="bg-nova-secondary border-b border-nova-green/20 px-4 sm:px-6 py-5 flex-shrink-0">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Left: Language selector */}
          <div className="flex items-center">
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                disabled={hasUserSentMessage || isLoading}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-nova-bg/50 transition-colors border border-nova-green/20 ${hasUserSentMessage || isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-nova-bg hover:border-nova-green/40'
                  }`}
                type="button"
                aria-label="Select language"
              >
                <span className="text-2xl">{LANGUAGES.find(l => l.code === selectedLanguage)?.flag}</span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>

              {showLanguageDropdown && !hasUserSentMessage && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-nova-bg border-2 border-nova-green rounded-lg shadow-2xl z-50 max-h-80 overflow-y-auto">
                  {LANGUAGES.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        if (onLanguageChange && language.code !== selectedLanguage) {
                          onLanguageChange(language.code);
                        }
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-nova-green/10 transition-colors ${selectedLanguage === language.code ? 'bg-nova-green/20' : ''
                        }`}
                      type="button"
                    >
                      <span className="text-2xl">{language.flag}</span>
                      <span className="text-sm text-white">{language.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Center: Title and info icon */}
          <div className="text-center min-w-0 flex-1">
            {isLoading && !surveyTitle ? (
              <div className="flex justify-center px-2">
                <div className="h-6 sm:h-7 bg-gradient-to-r from-nova-secondary via-nova-green/10 to-nova-secondary rounded-md w-48 animate-pulse"></div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 px-2">
                <h2 className="font-semibold text-white text-base sm:text-lg truncate">
                  {surveyTitle || 'NovaSage Survey'}
                </h2>
                {surveyDescription && (
                  <div className="relative" ref={tooltipRef}>
                    <button
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="text-nova-green hover:text-nova-green/80 transition-colors flex-shrink-0"
                      aria-label="Survey description"
                      type="button"
                    >
                      <Info size={18} className="sm:w-5 sm:h-5" />
                    </button>

                    {showTooltip && (
                      <div className="fixed sm:absolute top-20 sm:top-full left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 mt-2 sm:w-80 bg-nova-bg border-2 border-nova-green rounded-lg shadow-2xl p-4 z-50 max-h-60 overflow-y-auto">
                        <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                          {surveyDescription}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Placeholder for balance */}
          <div className="w-24"></div>
        </div>
      </div>

      {/* Messages Area - Centered with max width */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-3xl mx-auto px-6 py-6 pb-36 space-y-4">
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Skeleton loading state for first message */}
          {isLoading && messages.length === 0 && (
            <div className="flex justify-start">
              <div className="max-w-2xl bg-gradient-message-ai border border-nova-green/20 px-4 py-3 rounded-2xl shadow-lg w-full sm:w-3/4">
                <div className="space-y-2.5">
                  {[100, 92, 80].map((width, i) => (
                    <div key={i} className={`h-3 bg-white/5 rounded-full overflow-hidden relative`} style={{ width: `${width}%` }}>
                      <div className="h-full bg-gradient-to-r from-transparent via-nova-green/30 to-transparent animate-shimmer absolute inset-0 blur-sm" style={{ backgroundSize: '200% 100%' }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} min-w-0`}
            >
              <div
                className={`max-w-2xl min-w-0 ${message.sender === 'user'
                  ? 'bg-gradient-message-user text-white'
                  : 'bg-gradient-message-ai border border-nova-green/20 text-white'
                  } px-4 py-3 rounded-2xl overflow-hidden`}
              >
                {message.sender === 'ai' ? (
                  <div className="text-sm prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                )}

                {message.sender === 'ai' && message.toolResults && message.toolResults.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {message.toolResults
                      .filter(tr => tr.content && (tr.content.survey_url && tr.content.title))
                      .map((tr, idx) => (
                        <div key={idx} className="bg-nova-bg/50 rounded-lg p-3 border border-nova-green/20">
                          <SurveyQRCode
                            surveyUrl={tr.content.survey_url}
                            surveyTitle={tr.content.title}
                            size={200}
                          />
                        </div>
                      ))
                    }
                  </div>
                )}

                <p className={`text-xs mt-2 ${message.sender === 'user' ? 'text-white/70' : 'text-gray-400'
                  }`}>
                  {message.timestamp}
                </p>
              </div>
            </div>
          ))}

          {isSending && (
            <div className="flex justify-start">
              <div className="bg-nova-secondary border border-nova-green/20 px-4 py-3 rounded-2xl flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-nova-green" />
                <span className="text-sm text-gray-400">Thinking...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Floating at bottom, centered with max width */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-nova-bg via-nova-bg to-transparent pb-6 pt-8">
        <div className="max-w-3xl mx-auto px-6">
          <div>
            {/* End Conversation button and character count - in same row */}
            <div className="flex justify-between items-end mb-3">
              {!isEnded ? (
                <button
                  onClick={handleEndConversation}
                  disabled={isLoading}
                  className="flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-nova-secondary hover:bg-nova-secondary/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-nova-green/40 hover:border-nova-green shadow-sm hover:shadow-md"
                  title="End this conversation and submit your feedback"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>End Conversation</span>
                </button>
              ) : (
                <div></div>
              )}

              {/* Character count indicator - always rendered to prevent layout shift */}
              <div className={`text-xs transition-opacity min-h-[1.25rem] ${inputMessage.length > 0 ? 'opacity-100' : 'opacity-0'} ${inputMessage.length > MAX_MESSAGE_LENGTH ? 'text-red-400' : 'text-gray-400'}`}>
                {inputMessage.length > 0 && `${inputMessage.length} / ${MAX_MESSAGE_LENGTH} characters`}
              </div>
            </div>

            <div className="flex items-end space-x-2 bg-nova-secondary rounded-3xl shadow-lg border border-nova-green/20 p-2">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isEnded ? "Conversation ended." : "Type your answer..."}
                disabled={!sessionId || isEnded || isLoading}
                rows={1}
                className="flex-1 px-4 py-2 bg-transparent text-white focus:outline-none disabled:bg-transparent disabled:text-gray-500 placeholder-gray-500 resize-none overflow-y-auto max-h-40"
              />
              <button
                onMouseDown={preventBlur}
                onTouchStart={preventBlur}
                onClick={handleSendMessage}
                disabled={isSending || !sessionId || !inputMessage.trim() || inputMessage.length > MAX_MESSAGE_LENGTH || isEnded || isLoading}
                className="w-10 h-10 bg-gradient-primary text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Privacy disclaimer */}
            <div className="text-xs text-gray-500 text-center mt-3 px-2">
              Conversation will be shared with the survey creator.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyChatInterface;
